import { useEffect, useState } from "react"
import {
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Button,
} from "reactstrap"

const VehicleFilterDropdown = ({
  data = [],
  label = "Select Vehicle",
  onSelect = () => {},
  multiSelect = true,
  activeFilters = {},
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const [selectedItems, setSelectedItems] = useState([])

  // inside VehicleFilterDropdown
  useEffect(() => {
    const getSelectedFromFilters = () => {
      if (!activeFilters || Object.keys(activeFilters).length === 0) return []

      const items = []
      const brands = activeFilters.brand?.split(",") || []
      const models = activeFilters.model?.split(",") || []
      const engineTypes = activeFilters.engine_type?.split(",") || []

      data.forEach(item => {
        if (
          brands.includes(item.brand_slug) &&
          (!models.length || models.includes(item.model_slug)) &&
          (!engineTypes.length || engineTypes.includes(item.engine_type_slug))
        ) {
          items.push({
            brand: item.brand_slug,
            model: item.model_slug,
            engine_type: item.engine_type_slug,
            label: item.label,
            total: item.total,
          })
        }
      })

      return items
    }

    setSelectedItems(getSelectedFromFilters())
  }, [activeFilters, data])

  const toggle = () => setDropdownOpen(prev => !prev)

  // Convert selected items to URL parameters format
  const convertFiltersToParams = filters => {
    const params = {}

    filters.forEach(filter => {
      for (let key in filter) {
        if (filter.hasOwnProperty(key) && key !== "label" && key !== "total") {
          // Initialize as array if not exist
          if (!params[key]) {
            params[key] = []
          }

          // Add only if not already in array
          if (!params[key].includes(filter[key])) {
            params[key].push(filter[key])
          }
        }
      }
    })

    // Convert arrays to comma-separated strings
    for (let key in params) {
      params[key] = params[key].join(",")
    }

    return params
  }

  const handleSelect = item => {
    let newSelectedItems

    if (multiSelect) {
      // Check if item is already selected
      const isSelected = selectedItems.some(
        selected =>
          selected.brand === item.brand_slug &&
          selected.model === item.model_slug &&
          selected.engine_type === item.engine_type_slug
      )

      if (isSelected) {
        // Remove item if already selected
        newSelectedItems = selectedItems.filter(
          selected =>
            !(
              selected.brand === item.brand_slug &&
              selected.model === item.model_slug &&
              selected.engine_type === item.engine_type_slug
            )
        )
      } else {
        // Add new item
        newSelectedItems = [
          ...selectedItems,
          {
            brand: item.brand_slug,
            model: item.model_slug,
            engine_type: item.engine_type_slug,
            label: item.label,
            total: item.total,
          },
        ]
      }

      setSelectedItems(newSelectedItems)

      // Convert filters to URL params format
      const params = convertFiltersToParams(
        newSelectedItems.map(item => ({
          brand: item.brand,
          model: item.model,
          engine_type: item.engine_type,
        }))
      )

      onSelect(params)
    } else {
      // Single selection mode
      newSelectedItems = [
        {
          brand: item.brand_slug,
          model: item.model_slug,
          engine_type: item.engine_type_slug,
          label: item.label,
          total: item.total,
        },
      ]

      setSelectedItems(newSelectedItems)

      const params = convertFiltersToParams([
        {
          brand: item.brand_slug,
          model: item.model_slug,
          engine_type: item.engine_type_slug,
        },
      ])

      onSelect(params)
      setDropdownOpen(false)
    }
  }

  const handleClearAll = () => {
    setSelectedItems([])
    if (multiSelect) {
      onSelect({}) // Empty params
    } else {
      onSelect(null)
    }
  }

  const handleRemoveItem = (itemToRemove, e) => {
    e.stopPropagation()
    const newSelectedItems = selectedItems.filter(
      selected =>
        !(
          selected.brand === itemToRemove.brand &&
          selected.model === itemToRemove.model &&
          selected.engine_type === itemToRemove.engine_type
        )
    )

    setSelectedItems(newSelectedItems)

    const params = convertFiltersToParams(
      newSelectedItems.map(item => ({
        brand: item.brand,
        model: item.model,
        engine_type: item.engine_type,
      }))
    )

    onSelect(params)
  }

  const isItemSelected = item => {
    return selectedItems.some(
      selected =>
        selected.brand === item.brand_slug &&
        selected.model === item.model_slug &&
        selected.engine_type === item.engine_type_slug
    )
  }

  const getDisplayLabel = () => {
    if (selectedItems.length === 0) return label
    if (!multiSelect) return selectedItems[0].label

    const totalCount = selectedItems.length
    const totalVehicles = selectedItems.reduce(
      (sum, item) => sum + (item.total || 0),
      0
    )

    if (totalCount === 1) return selectedItems[0].label
    return `${totalCount} vehicles (${totalVehicles} total)`
  }

  return (
    <Dropdown
      isOpen={dropdownOpen}
      toggle={toggle}
      className="vehicle-filter-dropdown"
    >
      <DropdownToggle caret className="d-flex align-items-center">
        <i className="bx bx-filter-alt me-2"></i>
        <span className="me-2">{getDisplayLabel()}</span>
        {multiSelect && selectedItems.length > 0 && (
          <span className="badge bg-primary rounded-pill ms-1">
            {selectedItems.length}
          </span>
        )}
      </DropdownToggle>

      <DropdownMenu end={true} className="p-2" style={{ minWidth: "300px" }}>
        {/* Selected items summary for multi-select */}
        {multiSelect && selectedItems.length > 0 && (
          <div className="px-2 pb-2 mb-2 border-bottom">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">
                Selected ({selectedItems.length})
              </small>
              <button
                className="btn btn-sm btn-link text-danger p-0"
                onClick={handleClearAll}
              >
                Clear all
              </button>
            </div>
            <div
              className="selected-items-list"
              style={{ maxHeight: "100px", overflowY: "auto" }}
            >
              {selectedItems.map((item, idx) => (
                <div
                  key={idx}
                  className="d-flex justify-content-between align-items-center mb-1"
                >
                  <small
                    className="text-truncate"
                    style={{ maxWidth: "200px" }}
                  >
                    {item.label}
                  </small>
                  <button
                    className="btn btn-sm btn-link text-danger p-0 ms-2"
                    onClick={e => handleRemoveItem(item, e)}
                  >
                    <i className="bx bx-x"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter items */}
        <div style={{ maxHeight: "300px", overflowY: "auto" }}>
          {data.map((item, idx) => (
            <DropdownItem
              key={idx}
              onClick={() => handleSelect(item)}
              className={`d-flex justify-content-between align-items-center ${
                isItemSelected(item) ? "active" : ""
              }`}
              active={isItemSelected(item)}
              toggle={false}
            >
              <span className="d-flex align-items-center">
                {multiSelect && (
                  <i
                    className={`bx ${
                      isItemSelected(item)
                        ? "bx-checkbox-checked"
                        : "bx-checkbox"
                    } me-2`}
                  ></i>
                )}
                <span className="text-truncate" style={{ maxWidth: "200px" }}>
                  {item.label}
                </span>
              </span>
              <span className="badge bg-secondary rounded-pill ms-2 flex-shrink-0">
                {item.total}
              </span>
            </DropdownItem>
          ))}

          {data.length === 0 && (
            <DropdownItem disabled>No options available</DropdownItem>
          )}

          {/* close dropdown button */}
          <div className="px-2 py-1 border-top">
            <Button color="danger" size="sm" className="w-100" onClick={toggle}>
              Close
            </Button>
          </div>
        </div>
      </DropdownMenu>

      <style>{`
        .vehicle-filter-dropdown :global(.dropdown-toggle) {
          display: flex;
          align-items: center;
          gap: 4px;
          max-width: 300px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .vehicle-filter-dropdown :global(.dropdown-item.active) {
          background-color: rgba(13, 110, 253, 0.1);
          color: #0d6efd;
        }
        
        .vehicle-filter-dropdown :global(.dropdown-item:hover) {
          background-color: #f8f9fa;
        }
        
        .vehicle-filter-dropdown :global(.dropdown-item) {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        
        .selected-items-list::-webkit-scrollbar {
          width: 4px;
        }
        
        .selected-items-list::-webkit-scrollbar-thumb {
          background-color: #ccc;
          border-radius: 4px;
        }
      `}</style>
    </Dropdown>
  )
}

export default VehicleFilterDropdown
